import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { eventInfo, ticketInclusions, festivalSections, images } from '../data/mock';
import { Check, Ticket, Star, Music, Utensils, Wine, Palette, Crown, Mail, CheckCircle, Sparkles, PartyPopper, ExternalLink, ShieldCheck } from 'lucide-react';

const iconMap = {
  1: Wine,
  2: Crown,
  3: Music,
  4: Utensils,
  5: Palette,
  6: ShieldCheck
};

const sectionIcons = {
  "VIP Section": Crown,
  "Additive Free Alliance": Check,
  "Big Name Brands": Star,
  "Agave Spirits": Wine,
  "Mixto Tequilas Cocktails": Wine,
  "RTD Tequilas": Ticket,
  "Live Art & Flea": Palette
};

const NewsletterSignup = () => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (email) {
      setSubmitted(true);
      setEmail('');
    }
  };

  if (submitted) {
    return (
      <div className="flex items-center justify-center gap-2 text-green-400 py-4 animate-bounceIn">
        <PartyPopper className="w-6 h-6" />
        <span className="font-bold text-lg">You&apos;re on the list! 🎉</span>
        <PartyPopper className="w-6 h-6" />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
      <div className="relative flex-1">
        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50" />
        <Input
          type="email"
          placeholder="Get updates - enter email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="h-12 pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-pink-500 rounded-xl"
          required
        />
      </div>
      <Button 
        type="submit"
        className="h-12 bg-white/20 hover:bg-white/30 text-white font-bold rounded-xl px-6 transition-all duration-300 hover:scale-105 border border-white/20"
      >
        Subscribe
      </Button>
    </form>
  );
};

const TicketsSection = () => {
  const openTickets = () => {
    if (eventInfo.ticketUrl) {
      window.open(eventInfo.ticketUrl, '_blank');
    }
  };

  return (
    <section id="tickets" className="py-16 sm:py-24 bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950 relative overflow-hidden">
      {/* Animated decorative elements - hidden on mobile */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-pink-500/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2 animate-pulse hidden sm:block" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl translate-x-1/2 translate-y-1/2 animate-pulse hidden sm:block" style={{ animationDelay: '1s' }} />

      <div className="max-w-7xl mx-auto px-4 relative z-10">
        {/* Logo */}
        <div className="flex justify-center mb-6 sm:mb-8 animate-fadeInDown">
          <img
            src={images.logo}
            alt="Aloha Tequila & Arts Festival"
            className="h-28 sm:h-40 md:h-48 object-contain hover:scale-105 transition-transform duration-500"
          />
        </div>

        <h2 className="text-3xl sm:text-5xl md:text-6xl font-black text-white text-center mb-3 sm:mb-4 animate-fadeInUp">
          <span className="inline-flex items-center gap-2 sm:gap-3 flex-wrap justify-center">
            <Ticket className="w-7 h-7 sm:w-10 sm:h-10 text-pink-400 animate-pulse" />
            <span>Get Your Tickets!</span>
            <Ticket className="w-7 h-7 sm:w-10 sm:h-10 text-orange-400 animate-pulse hidden sm:block" />
          </span>
        </h2>
        
        <h3 className="text-base sm:text-xl md:text-2xl text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-orange-400 text-center mb-8 sm:mb-12 font-semibold animate-fadeInUp animation-delay-200 px-4">
          {eventInfo.edition} • {eventInfo.location}
        </h3>

        <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 items-start">
          {/* Left Column - Ticket Info */}
          <div className="animate-slideInLeft">
            <Card className="bg-white/5 backdrop-blur-sm border-white/10 p-5 sm:p-8 rounded-3xl hover-lift">
              <div className="text-center mb-6 sm:mb-8 pb-6 sm:pb-8 border-b border-white/10">
                <div className="inline-block bg-gradient-to-r from-green-500 to-emerald-500 text-white font-bold px-4 py-1 rounded-full text-sm mb-4 animate-pulse">
                  TICKETS ON SALE!
                </div>
                <h4 className="text-base sm:text-lg text-pink-400 font-semibold mb-2">{eventInfo.name}</h4>
                <p className="text-white text-xl sm:text-2xl font-bold">{eventInfo.date}</p>
                <p className="text-white text-base sm:text-lg">{eventInfo.time}</p>
                <p className="text-white/70 mt-2 text-sm sm:text-base">📍 {eventInfo.address}</p>
              </div>

              <h5 className="text-white font-bold text-base sm:text-lg mb-4 sm:mb-6 flex items-center gap-2">
                <PartyPopper className="w-5 h-5 text-pink-400" />
                Your ticket includes:
              </h5>
              
              <div className="space-y-5">
                {ticketInclusions.map((item, index) => {
                  const Icon = iconMap[item.id] || Check;
                  return (
                    <div 
                      key={item.id} 
                      className="flex gap-4 group p-3 rounded-xl hover:bg-white/5 transition-all duration-300"
                      style={{ animationDelay: `${index * 100}ms` }}
                    >
                      <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-pink-500 to-orange-500 rounded-2xl flex items-center justify-center group-hover:scale-110 group-hover:rotate-6 transition-all duration-300 shadow-lg">
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h6 className="text-white font-semibold mb-1 group-hover:text-pink-400 transition-colors">{item.title}</h6>
                        <p className="text-white/60 text-sm leading-relaxed">{item.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          </div>

          {/* Right Column - Buy Tickets & Info */}
          <div className="space-y-8 animate-slideInRight">
            <Card className="bg-white/5 backdrop-blur-sm border-white/10 p-4 sm:p-8 rounded-3xl hover-lift">
              <h5 className="text-white font-bold text-base sm:text-lg mb-4 sm:mb-6 flex items-center gap-2">
                <Star className="w-5 h-5 text-orange-400" />
                Festival Sections
              </h5>
              <div className="grid grid-cols-1 gap-2 sm:gap-3">
                {festivalSections.map((section, index) => {
                  const Icon = sectionIcons[section.name] || Check;
                  return (
                    <div 
                      key={index} 
                      className="flex items-start gap-2 sm:gap-3 p-3 sm:p-4 bg-gradient-to-br from-white/5 to-white/0 rounded-xl sm:rounded-2xl group hover:from-pink-500/20 hover:to-orange-500/10 transition-all duration-300 cursor-default border border-white/5 hover:border-pink-500/30"
                    >
                      <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-pink-400 mt-0.5 group-hover:scale-125 transition-transform flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-white font-medium text-sm group-hover:text-pink-400 transition-colors">{section.name}</p>
                        <p className="text-white/50 text-xs truncate">{section.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>

            {/* Featured Tequila Lineup CTA */}
            <Card className="bg-gradient-to-br from-pink-500/15 via-orange-500/10 to-amber-500/15 backdrop-blur-sm border border-pink-500/30 p-5 sm:p-6 rounded-3xl hover-lift">
              <div className="flex items-start justify-between gap-4 mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 bg-gradient-to-br from-pink-500 to-orange-500 rounded-xl flex items-center justify-center shadow-lg shadow-pink-500/30 shrink-0">
                    <Wine className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h5 className="text-white font-bold text-base sm:text-lg leading-tight">
                      Premium Tequilas Featured
                    </h5>
                    <p className="text-pink-300 text-xs font-semibold">46 brands · 113 pours · 32 VIP-only · Sat, June 20</p>
                  </div>
                </div>
              </div>
              <p className="text-white/70 text-sm leading-relaxed mb-4">
                Tequila Ocho, Código 1530, El Tesoro, Komos, Don Fulano, G4, Herradura, Partida, Milagro, Del Maguey, Xicaru &amp; more — every brand with direct links to their website.
              </p>
              <Link
                to="/tequila"
                data-testid="tickets-tequila-list-cta"
                className="inline-flex items-center gap-2 bg-gradient-to-r from-pink-500 via-orange-500 to-amber-500 hover:from-pink-600 hover:via-orange-600 hover:to-amber-600 text-white font-bold text-sm px-5 py-2.5 rounded-full shadow-lg shadow-pink-500/30 transition-all hover:scale-105"
              >
                See the full lineup
                <ExternalLink className="w-4 h-4" />
              </Link>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TicketsSection;
